import { ArrowRight, Clock, Handshake, TrendingUp, type LucideIcon } from 'lucide-react';

interface JamesEmptyStateProps {
  onSuggestionClick?: (suggestion: string) => void;
  compact?: boolean;
}

/** Questions people at SEQUESTO actually ask, not placeholder companies. */
const suggestions: { title: string; description: string; icon: LucideIcon }[] = [
  {
    title: 'What’s our MRR this month?',
    description: 'Recurring revenue from Odoo, with the change since last month.',
    icon: TrendingUp,
  },
  {
    title: 'Which deals are stale?',
    description: 'Open deals in Attio whose stage hasn’t moved in 90 days, the same rule as Sales.',
    icon: Clock,
  },
  {
    title: 'Which deals close this quarter?',
    description: 'Stage, owner, amount and next step for the quarter.',
    icon: Handshake,
  },
];

export function JamesEmptyState({ onSuggestionClick, compact }: JamesEmptyStateProps) {
  if (compact) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-6">
        <img src="/sequesto-icon.png" alt="Sequesto" className="h-9 w-9 select-none rounded-xl" draggable={false} />
        <h3 className="mt-3 text-heading-md text-text-primary">Ask James anything</h3>
        <p className="mt-1 text-center text-body-sm text-text-secondary">
          Pipeline, revenue, customers and calls, in plain English.
        </p>
        {/* The same suggestion cards as the full /james page, stacked to fit the panel */}
        <div className="mt-5 grid w-full max-w-sm gap-2">
          {suggestions.map((s) => (
            <SuggestionCard key={s.title} {...s} compact onClick={() => onSuggestionClick?.(s.title)} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center overflow-hidden px-6 py-10">
      <div className="mx-auto w-full max-w-3xl">
        <div className="flex items-center gap-3">
          <img src="/sequesto-icon.png" alt="Sequesto" className="h-10 w-10 select-none rounded-xl" draggable={false} />
          <div>
            <h3 className="text-heading-lg text-text-primary">Ask James</h3>
            <p className="text-body-sm text-text-secondary">Pipeline, revenue, customers and calls, in plain English.</p>
          </div>
        </div>

        <p className="mt-8 text-body-xs font-medium text-text-tertiary">Try asking</p>
        <div className="mt-2 grid gap-3 sm:grid-cols-3">
          {suggestions.map((s) => (
            <SuggestionCard key={s.title} {...s} onClick={() => onSuggestionClick?.(s.title)} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** One suggested question: a hairline card with an icon, the question and what it pulls. */
function SuggestionCard({
  title,
  description,
  icon: Icon,
  compact = false,
  onClick,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  compact?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex rounded-2xl border border-border-light bg-surface-primary text-left transition-colors hover:border-border ${
        compact ? 'items-start gap-3 p-3' : 'flex-col items-start gap-3 p-4'
      }`}
    >
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-surface-secondary text-text-secondary group-hover:bg-surface-inverse group-hover:text-text-inverse">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 pr-5">
        <p className="text-body-sm font-medium text-text-primary">{title}</p>
        <p className="mt-1 text-body-xs text-text-tertiary">{description}</p>
      </div>
      <ArrowRight
        className={`absolute right-4 h-4 w-4 text-text-tertiary opacity-0 transition-opacity group-hover:opacity-100 ${
          compact ? 'top-3' : 'top-4'
        }`}
        aria-hidden
      />
    </button>
  );
}
